import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabaseApi, Project, Category } from '../services/supabase';
import { 
  LayoutDashboard, 
  FolderOpen, 
  Tag, 
  Plus, 
  LogOut, 
  DollarSign,
  Users,
  TrendingUp
} from 'lucide-react';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8fafc;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LogoutButton = styled.button`
  background: none;
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;

const MainContent = styled.div`
  display: flex;
  min-height: calc(100vh - 70px);
`;

const Sidebar = styled.aside`
  width: 250px;
  background: white;
  border-right: 1px solid #e2e8f0;
  padding: 24px 0;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  color: #64748b;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    color: #667eea;
  }
  
  &.active {
    background: #f1f5f9;
    color: #667eea;
    border-right: 3px solid #667eea;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 24px;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
  margin-bottom: 24px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #1e293b;
  margin-bottom: 4px;
`;

const StatChange = styled.div<{ positive: boolean }>`
  font-size: 0.875rem;
  color: ${props => props.positive ? '#10b981' : '#ef4444'};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RecentSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProjectCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
`;

const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 0.875rem;
  color: #64748b;
`;

const AddButton = styled(Link)`
  background: #667eea;
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const Dashboard: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsData, categoriesData] = await Promise.all([
          supabaseApi.getProjects(),
          supabaseApi.getCategories()
        ]);
        setProjects(projectsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const recentProjects = projects.slice(0, 5);
  const totalBudget = projects.reduce((sum, project) => {
    const budget = project.budget ? parseInt(project.budget.replace(/[^0-9]/g, '')) : 0;
    return sum + budget;
  }, 0);

  if (loading) {
    return (
      <DashboardContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Loading...
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <HeaderContent>
          <Logo>🚀 Portfolio Admin</Logo>
          <HeaderActions>
            <LogoutButton onClick={handleLogout}>
              <LogOut size={16} />
              Logout
            </LogoutButton>
          </HeaderActions>
        </HeaderContent>
      </Header>

      <MainContent>
        <Sidebar>
          <NavItem to="/" className="active">
            <LayoutDashboard size={20} />
            Dashboard
          </NavItem>
          <NavItem to="/projects">
            <FolderOpen size={20} />
            Projects
          </NavItem>
          <NavItem to="/categories">
            <Tag size={20} />
            Categories
          </NavItem>
        </Sidebar>

        <Content>
          <PageTitle>Dashboard Overview</PageTitle>

          <StatsGrid>
            <StatCard>
              <StatHeader>
                <StatTitle>Total Projects</StatTitle>
                <StatIcon color="#667eea">
                  <FolderOpen size={20} />
                </StatIcon>
              </StatHeader>
              <StatValue>{projects.length}</StatValue>
              <StatChange positive={true}>
                <TrendingUp size={16} />
                +12% from last month
              </StatChange>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatTitle>Categories</StatTitle>
                <StatIcon color="#10b981">
                  <Tag size={20} />
                </StatIcon>
              </StatHeader>
              <StatValue>{categories.length}</StatValue>
              <StatChange positive={true}>
                <TrendingUp size={16} />
                +3 new categories
              </StatChange>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatTitle>Total Budget</StatTitle>
                <StatIcon color="#f59e0b">
                  <DollarSign size={20} />
                </StatIcon>
              </StatHeader>
              <StatValue>${totalBudget.toLocaleString()}</StatValue>
              <StatChange positive={true}>
                <TrendingUp size={16} />
                +8% from last month
              </StatChange>
            </StatCard>

            <StatCard>
              <StatHeader>
                <StatTitle>Active Clients</StatTitle>
                <StatIcon color="#8b5cf6">
                  <Users size={20} />
                </StatIcon>
              </StatHeader>
              <StatValue>{new Set(projects.map(p => p.client)).size}</StatValue>
              <StatChange positive={true}>
                <TrendingUp size={16} />
                +5 new clients
              </StatChange>
            </StatCard>
          </StatsGrid>

          <RecentSection>
            <SectionTitle>
              <FolderOpen size={20} />
              Recent Projects
              <AddButton to="/add-project">
                <Plus size={16} />
                Add Project
              </AddButton>
            </SectionTitle>
            
            {recentProjects.map((project) => (
              <ProjectCard key={project.id}>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectMeta>
                  <span>📅 {project.duration || 'N/A'}</span>
                  <span>💰 {project.budget || 'N/A'}</span>
                  <span>👤 {project.client || 'N/A'}</span>
                  <span>🏷️ {project.category?.name || 'Uncategorized'}</span>
                </ProjectMeta>
              </ProjectCard>
            ))}
          </RecentSection>
        </Content>
      </MainContent>
    </DashboardContainer>
  );
};

export default Dashboard;
